/* eslint-disable react/prop-types */
import styled from '@emotion/styled';
import Tags from '@base/Tags/Tags';
import P2 from '@base/P2/P2';
import H6 from '@base/H6/H6';
import Link from '@base/Link/Link';
import Thumbnail from '@base/Thumbnail/Thumbnail';
import getTopicGroupAssetObj from '@cont/Syllabus/constants/getTopicGroupAssetObj';
import { isSameDay, getTime, getFullDate } from '@utils/dates';
import { textTruncate } from '@utils/textHelper';
import Course, { getLinkPath, getLink } from '@models/Course';
import { Spacings, breakpoints } from '@styles/index';
import Card from './Card';
import AuthorHeader from './AuthorHeader';
import CardHeader from './CardHeader';
import MessageDivider from './MessageDivider';

const CardWrapper = styled.div``;

const StyledCard = styled(Card)`
  margin-top: 16px;
`;

const CourseCard = styled.div`
  display: flex;
`;

const CardInfo = styled.div`
  margin-left: 16px;
  max-width: 250px;
`;

const BreadCrumbs = styled.div`
  padding-bottom: ${Spacings.SPACING_1B};
  display: flex;
  align-items: center;
`;

const CourseThumbnail = styled(Thumbnail)`
  width: 144px;
  height: 80px;
  border-radius: ${Spacings.SPACING_2B};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    min-width: 44px;
    width: 44px;
    height: 44px;
    border-radius: 8px;
  }
`;

const CourseTitle = styled(H6)`
  color: var(--color-text-primary);
`;

const EducatorName = styled(P2)`
  color: var(--color-text-primary);
`;

const CourseTime = styled.div`
  font-size: 12px;
  line-height: 150%;
  color: var(--color-text-secondary);
  margin: 8px 0px;
`;

const TopicGroupTag = styled(Tags)`
  margin-left: ${Spacings.SPACING_2B};
  cursor: pointer;
  color: ${({ $color }) => $color};
`;

const NewCourseCard = ({ story }) => {
  const { message, created_at, object, author, sub_type } = story;

  const courseData = Course.getEntityData({ ...object, ...object.programme });

  const getCourseInfo = () => {
    const { isOngoing, isCompleted, isUpcoming, isLive, nextTrack } =
      courseData;
    if (!nextTrack || !nextTrack.item) return '';
    const liveLessonRank = nextTrack?.item?.rank;
    const liveAt = nextTrack?.item?.live_at;
    const liveLessonStartTime = new Date(liveAt);
    if (isLive) {
      return `Lesson ${liveLessonRank} • Started at ${getTime(liveAt)}`;
    }
    if (isOngoing) {
      const isToday = isSameDay(liveLessonStartTime, new Date());
      if (isToday)
        return `Lesson ${liveLessonRank} • Today, ${getTime(liveAt)}`;
      return `Lesson ${liveLessonRank} • ${getFullDate(liveAt)} ${getTime(
        liveAt
      )}`;
    }
    if (isUpcoming) {
      const {
        itemCount: totalLessons,
        liveClassCount,
        startsAt,
        isSpecial
      } = courseData;
      if (isSpecial) return `Starts on ${getFullDate(startsAt)}`;
      return `Starts on ${getFullDate(startsAt)} • ${
        totalLessons || liveClassCount || 0
      } lessons`;
    }
    if (isCompleted) {
      const {
        itemCount: totalLessons,
        liveClassCount,
        endsAt,
        isSpecial
      } = courseData;
      if (isSpecial) return `Ended on ${getFullDate(endsAt)}`;
      return `Ended on ${getFullDate(endsAt)} • ${
        totalLessons || liveClassCount || 0
      } lessons`;
    }
  };

  const CardHead = () => {
    if (sub_type === 1) {
      return (
        <CardHeader
          message={message}
          created_at={created_at}
          img="newclass.svg"
        />
      );
    }
    return (
      <AuthorHeader
        author={author}
        created_at={created_at}
        subtitle={courseData.name}
      />
    );
  };
  const { color } = getTopicGroupAssetObj(
    courseData.topicGroups[0]?.topology?.name
  );

  return (
    <CardWrapper>
      <CardHead />
      <StyledCard>
        {sub_type !== 1 && <MessageDivider message={message} />}
        <CourseCard>
          <CourseThumbnail imgUrl={courseData.thumbnail} />
          <CardInfo>
            <BreadCrumbs>
              {courseData.language ? (
                <Tags type="hollow">{courseData.language}</Tags>
              ) : null}
              {courseData.topicGroups?.[0]?.topology?.uid ? (
                <Link
                  href="/goal/[goalSlug]/[goalUID]/topic/[topicUid]"
                  as={`/goal/${courseData?.goal?.topology?.slug}/${courseData?.goal?.topology?.uid}/topic/${courseData.topicGroups[0]?.topology.uid}`}
                  anchorStyle="display: inline-block;max-width: 75%;line-height: 1;"
                >
                  <TopicGroupTag type="transparent" $color={color}>
                    {textTruncate(
                      courseData.topicGroups[0]?.topology.name || '',
                      24
                    )}
                  </TopicGroupTag>
                </Link>
              ) : null}
            </BreadCrumbs>
            <Link href={getLinkPath(courseData)} as={getLink(courseData)}>
              <CourseTitle lineClamp={2}>{courseData.name}</CourseTitle>
            </Link>
            <CourseTime>{getCourseInfo()}</CourseTime>
            <Link
              href="/[authorID]"
              as={`/@${courseData.authors?.[0]?.user?.username}`}
            >
              <EducatorName>
                {`${courseData.authors?.[0]?.user?.first_name} ${courseData.authors?.[0]?.user?.last_name}`}
              </EducatorName>
            </Link>
          </CardInfo>
        </CourseCard>
      </StyledCard>
    </CardWrapper>
  );
};

export default NewCourseCard;
